import { useId, type ReactElement, type ReactNode, type Ref } from "react";
import { FrameGrip, Window } from "./frames.tsx";
import { classes } from "./classes.ts";
import { logical } from "./metrics.ts";
import { sizeLabel, type FrameBox, type FramedWindowId, type PlaceRoom, type PlaceSize, type WindowPlace } from "./place.ts";
import { RESIZE_EDGES, useWindowFrame, type PlaceSource, type SizeSource } from "./window-frame.ts";

/**
 * A play window (ADR-061's amendments "a window can be moved", "a play window never stops play" and "every game
 * window moves and resizes"): the kit's `Window` standing on the page with no scrim, which the player keeps open
 * while playing (Inventory now; the character sheet, a trade window, a player card and the map when they are built).
 * The kit knows two kinds of window and the kind decides only what stands behind the window, no longer whether it
 * moves: a play window, this component, has no scrim and keeps the game's keys; a system window, `ModalWindow`,
 * stands on a scrim and holds the keyboard. Both are framed by the id their page gives them (`useWindowFrame`): the
 * title bar moves the window, its edges, corners and grip resize it, its place and size kept where the page says
 * (`place`, `size`) and held whole inside the room.
 *
 * Nothing else frames a window: `useWindowFrame` is not part of the kit's surface. The window holds nothing and names
 * no key; the page owns its layer, its focus and when it is open.
 */

/**
 * What a window's body or footer is: nodes, or a function of the size the window is drawn at, for a body that lays
 * itself out from its width and height (a grid that shows the columns that fit).
 */
export type Drawn = ReactNode | ((size: PlaceSize) => ReactNode);

/**
 * Makes what a window holds at the size it is drawn at.
 *
 * @param held The nodes, or the function that makes them.
 * @param size The size the window is drawn at.
 * @returns The nodes.
 */
function made(held: Drawn, size: PlaceSize): ReactNode {
  return typeof held === "function" ? held(size) : held;
}

/**
 * A play window.
 *
 * @param props The `id`, which gives the window its frame and its limits (`WINDOW_LIMITS`); the `title`, which names the window; the body, or a function of the size the window is drawn at (the window's own frame decides it, so a body never guesses); the `footer`, likewise, when the window has one; `onClose`, which the close button calls; `box`, where
 *   the window's layout puts it and how large it is, whole logical pixels; `room`, which a moved or resized window is held
 *   whole inside; `place` and `size`, where the page keeps the window's place and size (a visit's memory where it gives none); `host`, a ref to the positioned box the window
 *   stands in, when the page needs it; `className` for that box; and `data`, the `data-` attributes written on
 *   that box, by which the page's styles and checks find the window; and the body's `padding` in logical pixels, the
 *   kit's window padding unless given.
 * @returns The window in its place.
 * @example
 * <PlayWindow id="inventory" title="Inventory" onClose={close} box={bagWindowBox(area)} room={bagRoom(area)} place={windowPlace("inventory")}>
 *   <SlotGrid />
 * </PlayWindow>
 */
export function PlayWindow({ id, title, children, footer, onClose, box, room, place, size, host, className, data, padding }: { readonly id: FramedWindowId; readonly title: string; readonly children: Drawn; readonly footer?: Drawn | undefined; readonly onClose: () => void; readonly box: WindowPlace & PlaceSize; readonly room: PlaceRoom; readonly place?: PlaceSource | undefined; readonly size?: SizeSource | undefined; readonly host?: Ref<HTMLDivElement> | undefined; readonly className?: string | undefined; readonly data?: Readonly<Record<`data-${string}`, string>> | undefined; readonly padding?: number | undefined }): ReactElement {
  const { box: at, frame } = useWindowFrame({ id, place, size, home: box, room });
  return (
    <div ref={host} className={classes("ot-play-window", className)} data-kit="play-window" {...data} style={{ left: logical(at.left), top: logical(at.top) }}>
      <Window title={title} width={at.width} height={at.height} onClose={onClose} footer={footer === undefined ? undefined : made(footer, at)} frame={frame} padding={padding}>
        {made(children, at)}
      </Window>
    </div>
  );
}

/**
 * A float: a frameless play window, the map's (P2.12-T23, ADR-100 decision 49). It has no title bar, frame art or
 * footer: its content fills its box, drawn through two alpha ramps (a one dimensional CSS mask on each axis, never a
 * blur, so the content's pixels stay crisp and only their alpha falls) that fade its outer `rim` pixels to see-through,
 * under a 1 pixel outline. It is framed by the same hook as every play window, by the id its page gives it: the
 * control `strip` along its top is the drag handle (the whole strip, as a title bar is, taking the same arrow keys,
 * Home and double click), its outer rim is the eight handles of a resize, and the grip in the bottom right corner is
 * the window's last tab stop and takes the keys. The strip and the grip show while the pointer is over the float or
 * a control in it has focus, or while it is sized, and always on a coarse pointer. It catches the pointer over its box
 * alone and names no key.
 *
 * @param props The `id` (the frame's limits), the `label` that names the strip, `home`, where the page's layout puts
 *   the float, the `room` it is held inside, the kept `place` and `size`, the `strip` and the content `children`, each
 *   a function of the size the float is drawn at, the `rim` in logical pixels, `className` and the `data-` attributes
 *   for the page's styles and checks.
 * @returns The float in its place.
 * @example
 * <PlayFloat id="mapFloat" label="Map controls" rim={6} home={home} room={room} strip={(size) => <Buttons />}>
 *   {(size) => <MapView size={size} />}
 * </PlayFloat>
 */
export function PlayFloat({ id, label, home, room, place, size, strip, children, rim, host, className, data }: { readonly id: FramedWindowId; readonly label: string; readonly home: FrameBox; readonly room: PlaceRoom; readonly place?: PlaceSource | undefined; readonly size?: SizeSource | undefined; readonly strip: (size: PlaceSize) => ReactNode; readonly children: (size: PlaceSize) => ReactNode; readonly rim: number; readonly host?: Ref<HTMLDivElement> | undefined; readonly className?: string | undefined; readonly data?: Readonly<Record<`data-${string}`, string>> | undefined }): ReactElement {
  const { box, frame } = useWindowFrame({ id, place, size, home, room });
  const hintId = useId();
  const sizeId = useId();
  const move = frame?.move;
  return (
    <div ref={host} className={classes("ot-play-float", frame?.sizing === true && "ot-play-float--sizing", className)} data-kit="play-float" {...data} style={{ left: logical(box.left), top: logical(box.top), width: logical(box.width), height: logical(box.height) }}>
      <div className="ot-play-float__fade ot-play-float__fade--across" style={{ ["--ot-float-rim" as string]: logical(rim) }}>
        <div className="ot-play-float__fade ot-play-float__fade--down">{children(box)}</div>
      </div>
      <div
        className={classes("ot-play-float__strip", move !== undefined && "ot-play-float__strip--move")}
        data-kit="play-float-strip"
        role="group"
        aria-label={label}
        aria-describedby={move === undefined ? undefined : hintId}
        tabIndex={move === undefined ? undefined : 0}
        style={{ left: logical(rim), right: logical(rim), top: logical(rim) }}
        onMouseDown={move?.onMouseDown}
        onPointerDown={move?.onPointerDown}
        onPointerMove={move?.onPointerMove}
        onPointerUp={move?.onPointerUp}
        onPointerCancel={move?.onPointerCancel}
        onLostPointerCapture={move?.onLostPointerCapture}
        onDoubleClick={move?.onDoubleClick}
        onKeyDown={move?.onKeyDown}
      >
        {strip(box)}
      </div>
      {move === undefined ? null : (
        <span id={hintId} hidden>
          {move.hint}
        </span>
      )}
      {frame === null ? null : (
        <>
          {RESIZE_EDGES.map((edge) => (
            <div key={edge} className={`ot-play-float__edge ot-play-float__edge--${edge}`} data-kit="play-float-edge" data-edge={edge} aria-hidden="true" style={{ ["--ot-float-rim" as string]: logical(rim) }} {...frame.edges} />
          ))}
          <FrameGrip frame={frame} sizeId={sizeId} className="ot-play-float__grip" />
          {frame.sizing ? (
            <span className="ot-window__size ot-play-float__size" id={sizeId} role="status" data-kit="window-size">
              {sizeLabel(frame.size)}
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}
